import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useCanOnboard } from "@/hooks/usePermissions";
import { OnboardingAssistant } from "./OnboardingAssistant";

/**
 * OnboardingGate
 *
 * Decides whether to render the onboarding assistant overlay.
 * Renders via ReactDOM.createPortal so it floats above the entire
 * app shell (sidebar, header, content) without z-index conflicts.
 *
 * Trigger logic:
 *   - User must be able to onboard (Evo Administrator, or all five onboarding permissions)
 *   - Onboarding series must not be completed (hasCompleted)
 *   - Must not have dismissed in this session (hasDismissed)
 *   - Auto-shows on first load if trigger conditions are met
 */
export const OnboardingGate: React.FC = () => {
  const isEligible = useCanOnboard();
  const { isAssistantOpen, hasCompleted, hasDismissed, openAssistant } =
    useOnboarding();

  // Auto-trigger: show when eligible and the tenant series is neither completed nor
  // dismissed this session. hasCompleted starts true in live mode and re-evaluates
  // once the GraphQL onboarding read resolves.
  useEffect(() => {
    if (isEligible && !hasCompleted && !hasDismissed) {
      openAssistant();
    }
  }, [isEligible, hasCompleted, hasDismissed, openAssistant]);

  if (!isEligible) return null;
  if (!isAssistantOpen) return null;

  return ReactDOM.createPortal(<OnboardingAssistant />, document.body);
};
